// «إيه اللي بيتغير؟»: four cards hanging from a rule by thin connectors.
// REFERENCE.md §13, spec section 8. Copy: data.js, the CMS over OUTCOMES in copy.js.
//
// As the section scrolls in (desktop only), a bar fills the rule from the reading
// start, and each node and its card light up as the bar passes their column.
// That runs on the page's scroll frame, so the lit state is a class written
// directly on the element rather than React state. Lighting reverses on the way
// back up.

import { useRef } from "react";
import SectionHead from "./SectionHead";
import { OUTCOMES } from "./copy";
import { clamp01 } from "./motion/env";
import useSectionProgress from "./motion/useSectionProgress";
import "./Outcomes.css";

// Where each column's centre sits along the rule, from the reading start. rem
// scaling keeps these the same at every desktop width.
const LIT_AT = [0.118, 0.373, 0.627, 0.882];

// The bar starts at 30% of the section's progress and is full at 54%.
const fillFor = (p) => clamp01((p - 0.3) / 0.24);

export default function Outcomes({ outcomes = OUTCOMES }) {
    const sectionRef = useRef(null);
    const trackRef = useRef(null);
    const lit = useRef(-1);

    useSectionProgress(sectionRef, {
        smooth: true,
        onFrame: (p) => {
            const track = trackRef.current;
            if (!track) return;
            const fill = fillFor(p);
            track.style.setProperty("--ld-fill", fill.toFixed(4));
            const count = LIT_AT.filter((at) => fill >= at).length;
            if (count === lit.current) return;
            lit.current = count;
            track.querySelectorAll(".ld-outcomes__node, .ld-outcomes__card").forEach((el) => {
                el.classList.toggle("is-lit", Number(el.dataset.i) < count);
            });
        },
    });

    // The node above the hovered card answers it. Same reason for a direct class.
    const setHot = (i, on) => {
        trackRef.current?.querySelector(`.ld-outcomes__node[data-i="${i}"]`)?.classList.toggle("is-hot", on);
    };

    // The light that follows the pointer inside a card.
    const onPointerMove = (e) => {
        if (e.pointerType !== "mouse") return;
        const box = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--ld-mx", `${e.clientX - box.left}px`);
        e.currentTarget.style.setProperty("--ld-my", `${e.clientY - box.top}px`);
    };

    return (
        <section
            ref={sectionRef}
            className="ld-section ld-section--day ld-section--flush ld-outcomes"
            id="outcomes"
            aria-labelledby="ld-outcomes-title"
        >
            <div className="ld-decor" aria-hidden="true">
                <div className="ld-gridlines" style={{ "--ld-grid-at": "85% 85%" }} />
            </div>
            <div className="ld-container">
                <SectionHead id="ld-outcomes-title" title={outcomes.title} />
                <div className="ld-outcomes__track" ref={trackRef} data-ld="outcomes">
                    <div className="ld-rule ld-outcomes__rule" aria-hidden="true">
                        <span className="ld-outcomes__fill" />
                        <span className="ld-outcomes__nodes">
                            {outcomes.items.map(({ id }, i) => (
                                <span key={id} className="ld-outcomes__node" data-i={i} style={{ "--i": i }} />
                            ))}
                        </span>
                    </div>
                    <ul className="ld-outcomes__grid">
                        {outcomes.items.map(({ id, icon: Icon, title, line }, i) => (
                            <li
                                key={id}
                                className="ld-outcomes__card ld-glass-day ld-glass-day--strong"
                                data-i={i}
                                style={{ "--i": i }}
                                onPointerEnter={() => setHot(i, true)}
                                onPointerLeave={() => setHot(i, false)}
                                onPointerMove={onPointerMove}
                            >
                                <span className="ld-outcomes__catch" aria-hidden="true" />
                                <span className="ld-disc ld-outcomes__disc"><Icon aria-hidden="true" /></span>
                                <h3 className="ld-outcomes__title">{title}</h3>
                                <p className="ld-outcomes__line">{line}</p>
                                <span className="ld-outcomes__bar" aria-hidden="true" />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}
